import React from 'react';
import { Alert, Button } from '@parsina/framework';
import { CircleCheck, CircleX } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { EMPTY } from '../../lib/format';
import { Ident, KEYCLOAK_STEPS, accountName } from './shared';

/**
 * What a consolidation did (`AccountConsolidationReport`), and the audit line.
 *
 * The records half is done once the mutation returns; the Keycloak half stops at
 * the first failed step. So this is never a bare «انجام شد»: each step is shown
 * done or not, a failed one says so in a warning, and «اجرای دوباره» runs the
 * same pair again — the records then answer «none» and the remaining steps run.
 */
export default function ConsolidationResult({ entry, onRerun, onDismiss }) {
  const { t, has, formatNumber } = useTranslation();
  const { report, keep, drop } = entry;
  const resultKey = `accounts.result.kind.${report.result}`;
  const failed = !!report.failedStep;

  // A step is done when nothing failed, or when it ran before the one that did:
  // the BFF stops at the first failure, in this order.
  const stoppedAt = failed ? KEYCLOAK_STEPS.indexOf(report.failedStep) : -1;
  const done = (key) => !failed || KEYCLOAK_STEPS.indexOf(key) < stoppedAt;
  const DETAIL = {
    clear_phone: formerText(t, has, report.formerPhoneNumber),
    patient_id: has(`accounts.result.attribute.${report.patientIdAttribute}`)
      ? t(`accounts.result.attribute.${report.patientIdAttribute}`) : report.patientIdAttribute,
  };
  const steps = KEYCLOAK_STEPS.map((key) => ({ key, done: done(key), detail: done(key) ? DETAIL[key] : null }));

  return (
    <Alert
      variant={failed ? 'warning' : 'success'}
      title={failed ? t('accounts.result.partialTitle') : t('accounts.result.title')}
      className="mb-4"
      data-testid="accounts-result"
      data-result={report.result}
      action={(
        <div className="flex gap-2">
          {failed ? (
            <Button size="sm" variant="outline" onClick={onRerun} data-testid="accounts-result-rerun">
              {t('accounts.result.rerun')}
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={onDismiss} data-testid="accounts-result-dismiss">
            {t('accounts.result.dismiss')}
          </Button>
        </div>
      )}
    >
      <p data-testid="accounts-result-kind">
        {has(resultKey) ? t(resultKey) : String(report.result)}
        {' — '}
        {t('accounts.result.pair', { keep: accountName(keep), drop: accountName(drop) })}
      </p>

      <dl className="mt-2 grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-3 gap-y-1 text-sm">
        <dt>{t('accounts.result.patientId')}</dt>
        <dd><Ident value={report.patientId} data-testid="accounts-result-patient" /></dd>
        <dt>{t('accounts.result.mergeId')}</dt>
        <dd><Ident value={report.mergeId} data-testid="accounts-result-merge" /></dd>
        {report.mergedIds?.length ? (
          <>
            <dt>{t('accounts.result.mergedIds')}</dt>
            <dd className="flex flex-wrap gap-1.5">{report.mergedIds.map((id) => <Ident key={id} value={id} />)}</dd>
          </>
        ) : null}
        <dt>{t('accounts.result.proofsMoved')}</dt>
        <dd>{formatNumber(report.proofsMoved || 0)}</dd>
      </dl>

      <ul className="mt-2 space-y-1 text-sm" aria-label={t('accounts.result.stepsLabel')} data-testid="accounts-result-steps">
        {steps.map((s) => (
          <li key={s.key} className="flex flex-wrap items-center gap-1.5" data-testid={`accounts-step-${s.key}`} data-done={s.done ? 'true' : 'false'}>
            {s.done
              ? <CircleCheck size={15} aria-hidden className="text-[var(--chip-success-ink)]" />
              : <CircleX size={15} aria-hidden className="text-[var(--chip-danger-ink)]" />}
            <span>{t(`accounts.result.steps.${s.key}`)}</span>
            <span className="sr-only">{s.done ? t('accounts.result.done') : t('accounts.result.notDone')}</span>
            {s.detail ? <span className="text-muted-foreground">{`· ${s.detail}`}</span> : null}
          </li>
        ))}
      </ul>

      {failed ? (
        <p className="mt-2 text-sm font-semibold" data-testid="accounts-result-failed">
          {t('accounts.result.failedAt', {
            step: has(`accounts.result.steps.${report.failedStep}`)
              ? t(`accounts.result.steps.${report.failedStep}`) : report.failedStep,
          })}
        </p>
      ) : null}

      {report.notes?.length ? (
        <ul className="mt-2 list-disc ps-5 text-sm" data-testid="accounts-result-notes">
          {/* The BFF's own sentences: server text, shown as it came. */}
          {report.notes.map((n) => <li key={n} dir="auto">{n}</li>)}
        </ul>
      ) : null}

      <AuditLine entry={entry} />
    </Alert>
  );
}

function formerText(t, has, value) {
  const key = `accounts.result.former.${value}`;
  return has(key) ? t(key) : value;
}

/**
 * The audit line, as the operator should quote it in a ticket: who, when, what,
 * why, and the event id the service recorded. The BFF writes its own `[audit]`
 * line with the same facts (never a number); this is the operator's copy.
 */
export function AuditLine({ entry }) {
  const { t, formatDateTime } = useTranslation();
  return (
    <p className="mt-3 border-t border-border pt-2 text-xs text-muted-foreground" data-testid="accounts-audit">
      {t(entry.kind === 'release' ? 'accounts.audit.release' : 'accounts.audit.consolidate', {
        actor: entry.actor || t('accounts.audit.unknownActor'),
        at: formatDateTime(entry.at),
        keep: accountName(entry.keep),
        drop: accountName(entry.drop),
        record: entry.record?.fullName || entry.record?.id || EMPTY,
        holder: entry.holderLabel || EMPTY,
        reason: entry.reason,
        event: entry.report?.eventId || EMPTY,
      })}
    </p>
  );
}
